import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, CheckCircle2, Lock } from "lucide-react";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COVER =
  "https://static.prod-images.emergentagent.com/jobs/97f7bd52-49f9-4734-ba2f-dd76d8345fa9/images/cda375c1d1324053d10fa82f5c704c4de09899ee7e0dd637cb6218df08930656.jpeg";

export default function GuideSection() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Inserisci una email valida");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/guide`, { email: email.trim() });
      if (res.data.email_status?.paziente) {
        setSent(true);
        toast.success("Guida inviata! Controlla la tua email.");
      } else {
        toast.error("Email temporaneamente non disponibile, riprova tra poco.");
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Invio non riuscito, riprova tra poco.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="guida" className="relative py-28 overflow-hidden">
      <div className="absolute -bottom-32 -right-40 w-[500px] h-[500px] rounded-full bg-sage/15 blur-[140px]" />
      <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal className="relative order-2 lg:order-1">
          <div className="absolute -inset-3 rounded-[2.2rem] border border-gold/25 translate-x-4 translate-y-4" />
          <div className="relative rounded-[2rem] overflow-hidden border border-gold/30 shadow-[0_30px_80px_rgba(0,0,0,0.5)] w-full max-w-[420px] mx-auto aspect-[848/1264]">
            <img
              src={COVER}
              alt="Copertina della Guida anti-tabù"
              className="w-full h-full object-cover"
            />
          </div>
        </Reveal>

        <div className="order-1 lg:order-2">
          <Reveal>
            <p className="eyebrow">Regalo per te</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-4 tracking-tight">
              La Guida anti-tabù, <span className="italic text-gold">gratis.</span>
            </h2>
            <p className="mt-5 text-lg text-silver font-light leading-relaxed">
              Un piccolo libretto per conoscere le regole d'oro di ogni giorno, capire quando
              chiamare il medico e abbattere le paure più comuni. Lascia la tua email e te la invio
              subito.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            {sent ? (
              <div
                className="mt-8 flex items-center gap-3 rounded-2xl border border-sagelight/40 bg-sage/15 px-5 py-4"
                data-testid="guide-success-message"
              >
                <CheckCircle2 size={20} className="text-sagelight shrink-0" />
                <p className="text-sm text-silver">
                  Fatto! Ti ho inviato la guida a <span className="text-ivory">{email}</span>{" "}
                  (controlla anche lo spam).
                </p>
              </div>
            ) : (
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  data-testid="guide-input-email"
                  placeholder="La tua email *"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="field-dark px-5 py-3.5 text-sm flex-1"
                />
                <button
                  onClick={submit}
                  disabled={sending}
                  data-testid="guide-submit-button"
                  className="flex items-center justify-center gap-2 bg-gold text-ink font-semibold rounded-full px-7 py-3.5 hover:bg-goldlight transition-colors disabled:opacity-50"
                >
                  {sending ? (
                    "Invio…"
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send size={16} /> Inviami la guida
                    </span>
                  )}
                </button>
              </div>
            )}
            <p className="flex items-center gap-2 text-[11px] text-silver/70 mt-4">
              <Lock size={12} /> Uso la tua email solo per inviarti la guida. Niente spam, mai.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
